import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";

import {
  setAcceptedNewsletter,
  setAcceptedTerms,
  setCustomerInfo,
  startEmailValidation,
  setOverlayValue,
  invalidEmailValidation,
  successFullEmailValidation,
  removeReservation,
} from "store";
import { currentLanguage, capitalize, validateEmailFormat } from "helpers";
import WP from "WP";
import visbook from "api/visbook";

import CountrySelect from "./CountrySelect";
import Container from "partials/Container";
import GridElement from "partials/GridElement";
import Button from "partials/Button";
import CancelOrderTerms from "partials/CancelOrderTerms";
import Overlay from "partials/Overlay";
import CartSummary from "partials/CartSummary";
import ErrorBoundary from "partials/ErrorBoundary";
import Footer from "partials/Footer";

import Checked from "images/checked.svg";
import Media from "css/Media";

const InputField = (props) => {
  const [validated, setValidated] = useState(true);
  const [value, setValue] = useState(props.value);

  useEffect(() => {
    if (!props.fieldsValidated) {
      setValidated(value.length > 1);
    }
  }, [props.fieldsValidated]);

  const getErrorText = () => {
    switch (props.id) {
      case "email":
        return <p>{WP.trans.invalidEmail}</p>;
      case "phone":
        return <p>{WP.trans.invalidTel}</p>;
      case "address":
        return <p>{WP.trans.invalidAdress}</p>;
      case "zipCode":
        return <p>{WP.trans.invalidPostal}</p>;
      default:
        return (
          <p>
            {WP.trans.invalidBlank}
            {props.label.toLowerCase()}
          </p>
        );
    }
  };

  const handleBlur = (e) => {
    if (props.type === "email") {
      setValidated(validateEmailFormat(e.target.value));
    } else setValidated(e.target.value.length > 1);
  };

  const handleOnChange = (e) => {
    setValue(e.target.value);
    props.onChange(e);
  };

  return (
    <StyledLabel
      id={props.id}
      validated={validated}
      className={props.className}
    >
      <p>{props.label}</p>
      <input
        type={props.type}
        name={props.id}
        onChange={(e) => handleOnChange(e)}
        onBlur={(e) => handleBlur(e)}
        value={props.value}
      />
      {!validated && getErrorText()}
    </StyledLabel>
  );
};

export const CheckoutPage = (props) => {
  const [validEmailFormat, setValidEmailFormat] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [fieldsValidated, setFieldsValidated] = useState(true);

  const {
    customer,
    acceptedTerms,
    acceptedNewsletter,
    emailValidated,
    validationPending,
    reservations,
  } = props.booking;

  useEffect(() => {
    const { booking, history } = props;
    const { reservations } = booking;
    //if cart has content, proceed, else redirect to "/"
    window.scrollTo(0, 0);

    if (reservations.length === 0) {
      history.push("/");
    }
  }, []);

  useEffect(() => {
    validateEmail();
  }, [props.booking.customer.email]);

  useEffect(() => {
    if (submitting) {
      if (
        !customer.address ||
        !customer.city ||
        !customer.country ||
        !customer.email ||
        !customer.firstName ||
        !customer.lastName ||
        !customer.phone ||
        !customer.zipCode ||
        !acceptedTerms
      ) {
        setFieldsValidated(false);
        setSubmitting(false);
      } else {
        setSubmitted(true);
      }
    }
  }, [submitting]);

  useEffect(() => {
    if (submitted) {
      visbook
        .startEmailValidation(props.booking.customer.email)
        .then((r) => {
          props.startEmailValidation();
        })
        .catch((r) => {
          props.invalidEmailValidation();
        });
    }
  }, [submitted]);

  const handleTermsClick = () => {
    props.setOverlayValue({
      visible: true,
      component: <CancelOrderTerms />,
    });
  };

  const handleOnChange = (e) => {
    props.setCustomerInfo({
      [e.target.name]: e.target.value,
    });
  };

  const handleCountryChange = (e) => {
    props.setCustomerInfo({
      country: e,
    });
  };

  const toggleTerms = () => {
    props.setAcceptedTerms(!props.booking.acceptedTerms);
  };

  const toggleNewsletter = () => {
    props.setAcceptedNewsletter(!props.booking.acceptedNewsletter);
  };

  const removeReservation = (reservationIndex) => {
    props.removeReservation(reservationIndex);
  };

  const clickHandler = () => {
    setSubmitting(true);
  };

  const confirmEmailToken = () => {
    const { emailValidationToken } = props.booking.customer;

    visbook
      .confirmEmailToken(emailValidationToken)
      .then((r) => {
        props.successFullEmailValidation();
      })
      .catch((r) => {
        props.invalidEmailValidation();
      });
  };

  const initiateCheckout = () => {
    const { booking } = props;
    visbook
      .checkout({
        reservations: booking.reservations.map((r) => {
          return {
            reservationId: r.reservationId,
            encryptedCompanyId: r.encryptedCompanyId,
          };
        }),
        amount: booking.reservations.reduce(
          (a, b) => a + b.room.availability.prices[0].calculatedPrice,
          0
        ),
        customer: booking.customer,
        acceptedTerms: booking.acceptedTerms,
      })
      .then((r) => {
        window.location.href = r.data.terminalUrl;
      });
  };

  const validateEmail = () => {
    if (props.booking.customer.email.length) {
      setValidEmailFormat(validateEmailFormat(props.booking.customer.email));
    }
  };

  if (reservations.length === 0) {
    return (
      <Wrapper>
        <Container grid>
          <InputGridElement
            colStart={{ default: 1, s: 1, l: 3 }}
            colEnd={{ default: 8, s: 11, l: 14 }}
            rowStart={1}
          >
            <h1>{WP.trans.cannotCompleteCheckout}</h1>
          </InputGridElement>
        </Container>
      </Wrapper>
    );
  }

  return (
    <React.Fragment>
      <Wrapper>
        <Container grid>
          <InputGridElement
            colStart={{ default: 1, s: 1, l: 3 }}
            colEnd={{ default: 8, s: 11, l: 14 }}
            rowStart={1}
          >
            <h1>
              {WP.trans.completeCheckout}{" "}
              <a
                href={
                  currentLanguage() === "no"
                    ? "/overnatting"
                    : "/en/accommodation"
                }
              >
                {WP.trans.addMoreRooms}
              </a>
            </h1>
          </InputGridElement>
          <InputGridElement
            colStart={{ default: 1, s: 1, l: 3 }}
            colEnd={{ default: 8, s: 11, l: 8 }}
            rowStart={{ default: 3, l: 2 }}
          >
            <InputField
              id='firstName'
              label={WP.trans.firstName}
              type='text'
              onChange={(e) => handleOnChange(e)}
              value={customer.firstName}
              fieldsValidated={fieldsValidated}
            />
          </InputGridElement>
          <InputGridElement
            colStart={{ default: 1, s: 1, l: 9 }}
            colEnd={{ default: 8, s: 11, l: 14 }}
            rowStart={{ default: 4, l: 2 }}
          >
            <InputField
              id='lastName'
              label={WP.trans.lastName}
              type='text'
              onChange={(e) => handleOnChange(e)}
              value={customer.lastName}
              fieldsValidated={fieldsValidated}
            />
          </InputGridElement>
          <InputGridElement
            colStart={{ default: 1, s: 1, l: 3 }}
            colEnd={{ default: 8, s: 11, l: 14 }}
            rowStart={{ default: 5, l: 3 }}
          >
            <InputField
              id='email'
              label={WP.trans.email}
              type='email'
              onChange={(e) => handleOnChange(e)}
              value={customer.email}
              validated={
                (validEmailFormat && !submitting) ||
                (validEmailFormat && customer.email.length && submitting)
              }
              fieldsValidated={fieldsValidated}
            />
          </InputGridElement>
          <InputGridElement
            colStart={{ default: 1, s: 1, l: 3 }}
            colEnd={{ default: 8, s: 11, l: 8 }}
            rowStart={{ default: 6, l: 4 }}
          >
            <InputField
              id='phone'
              label={WP.trans.phone}
              type='tel'
              onChange={(e) => handleOnChange(e)}
              value={customer.phone}
              fieldsValidated={fieldsValidated}
            />
          </InputGridElement>
          <InputGridElement
            colStart={{ default: 1, s: 1, l: 9 }}
            colEnd={{ default: 8, s: 11, l: 14 }}
            rowStart={{ default: 7, l: 4 }}
          >
            <CountrySelect
              onChange={(e) => handleCountryChange(e)}
              fieldsValidated={fieldsValidated}
              value={customer.country}
            />
          </InputGridElement>
          <InputGridElement
            colStart={{ default: 1, s: 1, l: 3 }}
            colEnd={{ default: 8, s: 11, l: 14 }}
            rowStart={{ default: 8, l: 5 }}
          >
            <InputField
              id='address'
              label={WP.trans.adress}
              type='text'
              onChange={(e) => handleOnChange(e)}
              value={customer.address}
              fieldsValidated={fieldsValidated}
            />
          </InputGridElement>
          <InputGridElement
            colStart={{ default: 1, s: 1, l: 3 }}
            colEnd={{ default: 8, s: 11, l: 8 }}
            rowStart={{ default: 9, l: 6 }}
          >
            <InputField
              id='zipCode'
              label={WP.trans.postal}
              type='number'
              onChange={(e) => handleOnChange(e)}
              value={customer.zipCode}
              fieldsValidated={fieldsValidated}
            />
          </InputGridElement>
          <InputGridElement
            colStart={{ default: 1, s: 1, l: 9 }}
            colEnd={{ default: 8, s: 11, l: 14 }}
            rowStart={{ default: 10, l: 6 }}
          >
            <InputField
              id='city'
              label={WP.trans.city}
              type='text'
              onChange={(e) => handleOnChange(e)}
              value={customer.city}
              fieldsValidated={fieldsValidated}
            />
          </InputGridElement>
          <GridElement
            colStart={{ default: 1, s: 1, l: 3 }}
            colEnd={{ default: 8, s: 11, l: 14 }}
            rowStart={{ default: 11 }}
          >
            <h2>{WP.trans.readAcceptTerms}</h2>
          </GridElement>
          <GridElement
            colStart={{ default: 1, s: 1, l: 3 }}
            colEnd={{ default: 8, s: 11, l: 14 }}
            rowStart={{ default: 12 }}
          >
            <StyledChecker
              terms
              validated={
                !fieldsValidated ? (acceptedTerms ? true : false) : true
              }
            >
              <input
                type='checkbox'
                onChange={toggleTerms}
                checked={acceptedTerms}
                validated={
                  !fieldsValidated ? (acceptedTerms ? true : false) : true
                }
              />
              {currentLanguage() === "no" ? (
                <p>
                  Jeg bekrefter at jeg har lest og godtatt Svalbard Adventures’{" "}
                  <span onClick={handleTermsClick}>
                    bestillings- og avbestillingsregler
                  </span>
                </p>
              ) : (
                <p>
                  I agree to Svalbard Adventures'{" "}
                  <span onClick={handleTermsClick}>
                    order and cancellation terms
                  </span>
                </p>
              )}
            </StyledChecker>
          </GridElement>
          <GridElement
            colStart={{ default: 1, s: 1, l: 3 }}
            colEnd={{ default: 8, s: 11, l: 14 }}
            rowStart={{ default: 13 }}
          >
            <StyledChecker validated>
              <input
                type='checkbox'
                onChange={toggleNewsletter}
                checked={acceptedNewsletter}
              />
              <p>{WP.trans.newsletterSignup}</p>
            </StyledChecker>
          </GridElement>
          <GridElement
            colStart={{ default: 1, s: 1, l: 3 }}
            colEnd={{ default: 8, s: 16, l: 14 }}
            rowStart={{ default: 14 }}
          >
            {validationPending ? (
              <StyledPendingToken>
                <div>
                  <p>{WP.trans.emailValidationTokenSent}</p>
                  <div>
                    <input
                      name='emailValidationToken'
                      onChange={(e) => handleOnChange(e)}
                      value={customer.emailValidationToken}
                    />
                    <StyledButton onClick={confirmEmailToken}>
                      {capitalize(WP.trans.confirm)}
                    </StyledButton>
                  </div>
                </div>
                {currentLanguage() === "no" ? (
                  <p>
                    Har du ikke mottatt noen epost? Eller ønsker å få sendt den
                    på nytt, kan du gjøre det ved å{" "}
                    <span onClick={clickHandler}>klikke her</span>.
                  </p>
                ) : (
                  <p>
                    If you have not received code or wish to send it again,{" "}
                    <span onClick={clickHandler}>click here</span>.
                  </p>
                )}
              </StyledPendingToken>
            ) : null}

            {!validationPending && !emailValidated && fieldsValidated ? (
              <StyledNext>
                <p>{WP.trans.checkDetailsBeforeProceed}</p>
                <StyledButton onClick={clickHandler}>
                  {capitalize(WP.trans.next)}
                </StyledButton>
              </StyledNext>
            ) : null}

            {!validationPending && !emailValidated && !fieldsValidated ? (
              <StyledNext>
                <StyledMessageBox>
                  <p>{WP.trans.someFieldsAreMissing}</p>
                </StyledMessageBox>
                <StyledButton onClick={clickHandler}>
                  {capitalize(WP.trans.next)}
                </StyledButton>
              </StyledNext>
            ) : null}

            {emailValidated ? (
              <StyledContinueCheckout>
                <p>{WP.trans.emailConfirmed}</p>
                <StyledButton onClick={initiateCheckout}>
                  {WP.trans.goToCheckout}
                </StyledButton>
              </StyledContinueCheckout>
            ) : null}
          </GridElement>
          {reservations.length > 0 ? (
            <CartSummary
              removeReservationFunc={(reservationIndex) =>
                removeReservation(reservationIndex)
              }
              reservations={reservations}
            />
          ) : null}
        </Container>
        <Overlay />
      </Wrapper>
      <ErrorBoundary for='Footer'>
        <Footer />
      </ErrorBoundary>
    </React.Fragment>
  );
};

const mapStateToProps = (state) => ({
  overlayValue: state.overlayValue,
  booking: state.booking,
});

const actions = {
  setOverlayValue,
  setCustomerInfo,
  startEmailValidation,
  invalidEmailValidation,
  successFullEmailValidation,
  setAcceptedTerms,
  setAcceptedNewsletter,
  removeReservation,
};

export default connect(mapStateToProps, actions)(withRouter(CheckoutPage));

const Wrapper = styled.div`
  padding-top: 140px;
  padding-bottom: 100px;

  ${Media.greaterThan("s")`
        padding-top: 240px;
    `}

  .container {
    grid-template-rows: repeat(9, "auto");

    input[type="checkbox"] {
      -webkit-appearance: none;
      width: 24px;
      height: 24px;
      border: 1px solid ${(props) => props.theme.colors.input};
      margin-right: 16px;
      cursor: pointer;
      transition: border 0.2s ease;
      flex-shrink: 0;

      &:checked {
        background-image: url(${Checked});
        background-repeat: no-repeat;
        background-position: center;
      }

      &:hover,
      &:focus {
        border-color: ${(props) => props.theme.colors.text};
      }
    }

    h1 {
      ${(props) => props.theme.fonts.h3}
      padding-bottom: 70px;

      a {
        &:hover,
        &:focus {
          text-decoration: none;
        }
      }
    }

    h2 {
      ${(props) => props.theme.fonts.h4}
      padding: ${(props) => props.theme.spacing.elementXS} 0 ${(props) =>
        props.theme.spacing.contentXL};

      ${Media.greaterThan("s")`
        padding: 0 0 ${(props) => props.theme.spacing.elementS} 0;
        line-height: 27px;
      `}
    }

    a,
    span {
      cursor: pointer;
      text-decoration: underline;
      color: ${(props) => props.theme.colors.button};
    }
  }
`;

const InputGridElement = styled(GridElement)`
  padding-bottom: ${(props) => props.theme.spacing.contentXL};

  ${Media.greaterThan("s")`
    padding-bottom: ${(props) => props.theme.spacing.elementXS};

    &:last-of-type {
        padding-bottom: ${(props) => props.theme.spacing.elementM};
    }
  `}
`;

const StyledLabel = styled.label`
  p {
    ${(props) => props.theme.fonts.h6}
    ${Media.greaterThan("s")`
      padding: 0 0 ${(props) => props.theme.spacing.contentM} ${(props) =>
      props.theme.spacing.contentM};
    `}
  }

  > p:first-of-type {
    color: ${(props) => props.theme.colors.text};
  }

  input {
    ~ p {
      color: ${(props) => props.theme.colors.danger};
      font-size: 14px;
      line-height: 16px;
      padding-top: 16px;

      ${Media.greaterThan("s")`
        font-size: 16px;
      `}
    }
  }

  input[type="text"],
  input[type="email"],
  input[type="number"],
  input[type="tel"] {
    width: 100%;
    height: ${(props) => props.theme.spacing.elementS};
    border: 1px solid
      ${(props) =>
        props.validated ? props.theme.colors.input : props.theme.colors.danger};
    box-shadow: none;
    -webkit-appearance: none;
    border-radius: 0;
    ${(props) => props.theme.fonts.p};
    font-size: 14px;
    padding: 0 25px;
    background-color: ${(props) => props.theme.colors.negative};
    transition: border 0.2s ease;

    &:hover,
    &:focus-within {
      border-color: ${(props) => props.theme.colors.text};
    }

    ${Media.greaterThan("s")`
      font-size: 16px;
    `}
  }
`;

const StyledChecker = styled.div`
  display: flex;
  align-items: flex-start;
  padding-bottom: ${(props) =>
    props.terms ? props.theme.spacing.contentL : props.theme.spacing.elementXL};

  p {
    font-size: 14px;
    line-height: 22px;
    flex-shrink: 1;
    color: ${(props) =>
      props.validated ? props.theme.colors.black : props.theme.colors.danger};
    ${Media.greaterThan("s")`
      align-items: center;
      font-size: 16px;
      line-height: 22px;
    `};
  }

  input {
    border-color: ${(props) =>
      props.validated
        ? props.theme.colors.black
        : props.theme.colors.danger}!important;
  }

  ${Media.greaterThan("s")`
    align-items: center;
  `}
`;

const StyledButton = styled(Button)`
  align-self: center;
  width: 50%;
  padding-top: 16px;
  padding-bottom: 16px;

  ${Media.greaterThan("s")`
    width: auto;
  `}
`;

const StyledNext = styled.div`
  display: flex;
  flex-direction: column;
  transition: opacity 0.2s ease;

  > p {
    padding-bottom: ${(props) => props.theme.spacing.elementXS};

    ${Media.greaterThan("s")`
      padding: 0;
    `}
  }

  ${Media.greaterThan("s")`
    align-items: center;
    padding-bottom: ${(props) => props.theme.spacing.elementXL};
    flex-direction: row;
    justify-content: space-between;
  `}
`;

const StyledMessageBox = styled.div`
  background-color: ${(props) => props.theme.colors.tooltip};
  padding: ${(props) => props.theme.spacing.contentL};
  border-radius: ${(props) => props.theme.spacing.contentM};
  margin-bottom: 50px;

  ${Media.greaterThan("s")`
    margin-bottom: 0;
    width: 100%;
    margin-right: 36px;
  `}
`;

const StyledPendingToken = styled.div`
  transition: opacity 0.2s ease;

  > div {
    background-color: ${(props) => props.theme.colors.tooltip};
    padding: ${(props) => props.theme.spacing.contentXL};
    flex-direction: column;

    > p {
      padding-bottom: ${(props) => props.theme.spacing.elementXS};

      ${Media.greaterThan("s")`
        padding-bottom: ${(props) => props.theme.spacing.contentXL};
      `}
    }

    > div {
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      input {
        flex-grow: 2;
        align-self: center;
        height: ${(props) => props.theme.spacing.elementS};
        border: 1px solid ${(props) => props.theme.colors.input};
        box-shadow: none;
        -webkit-appearance: none;
        border-radius: 0;
        ${(props) => props.theme.fonts.p};
        font-size: 14px;
        padding: 0 25px;
        background-color: ${(props) => props.theme.colors.negative};
        transition: border 0.2s ease;
        margin-bottom: 45px;
        width: 100%;

        &:hover,
        &:focus-within {
          border-color: ${(props) => props.theme.colors.text};
        }

        ${Media.greaterThan("s")`
          font-size: 16px;
          margin-right: 45px;
          margin-bottom: 0;
        `}
      }

      div {
        flex-grow: 1;
      }

      ${Media.greaterThan("s")`
        flex-direction: row;
      `}
    }
  }

  > p {
    padding-top: ${(props) => props.theme.spacing.contentL};

    span {
      color: ${(props) => props.theme.colors.text}!important;
    }
  }
`;

const StyledContinueCheckout = styled.div`
  display: flex;
  flex-direction: column;
  transition: opacity 0.2s ease;

  > p {
    padding-bottom: ${(props) => props.theme.spacing.elementXS};

    ${Media.greaterThan("s")`
      padding: 0;
    `}
  }

  ${Media.greaterThan("s")`
    align-items: center;
    padding-bottom: ${(props) => props.theme.spacing.elementXL};
    flex-direction: row;
    justify-content: space-between;
  `}
`;
